import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createInvitation } from '@services/api';
import { useCurrentUserAccount } from '@services/state';
import React from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  /** The firm whose account is being handed on. */
  firm: string;
  onClose: () => void;
};

/** The roles a firm's buyer tends to hold. Free text stays available through Other. */
const ROLES = ['OwnerPrincipal', 'Operations', 'Finance', 'ItAdmin', 'Other'] as const;

/**
 * Naming the person at the firm who can actually make this call.
 *
 * Sent by someone who is already working here and knows they are not the buyer. It creates an
 * invitation carrying the right to claim the account - not ownership itself. The person named has
 * to accept before anything moves, which is the whole point: a colleague nominating you is not the
 * same as you agreeing to be responsible for your company's billing.
 *
 * Firm-scoped. The client who started the chain is not told and is not copied: who owns this
 * firm's account, and what it costs, is not the client's business.
 */
export const InviteBuyerModal: React.FC<Props> = ({ firm, onClose }) => {
  const { t } = useTranslation();
  const [account] = useCurrentUserAccount();

  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [role, setRole] = React.useState<string>(ROLES[0]);
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState<string>();

  const valid = name.trim().length > 1 && /.+@.+\..+/.test(email.trim());

  const send = React.useCallback(async () => {
    if (!account?.id || !valid || busy) return;

    setBusy(true);
    setError(undefined);
    try {
      const [givenName, ...rest] = name.trim().split(/\s+/);
      await createInvitation(account.id, {
        givenName,
        familyName: rest.join(' '),
        emailAddress: email.trim(),
        grantsOwnershipClaim: true,
        metadata: note.trim() ? { buyerRole: role, note: note.trim() } : { buyerRole: role },
      });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Ownership.Errors.Unknown'));
    } finally {
      setBusy(false);
    }
  }, [account?.id, busy, email, name, note, role, t, valid]);

  if (sent) {
    return (
      <div className="cc-own-modal-scrim" role="dialog" aria-modal="true">
        <div className="cc-own-modal">
          <button type="button" className="cc-own-x" onClick={onClose} aria-label={t('General.Close')}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
          <h2>{t('Ownership.InviteBuyer.SentTitle')}</h2>
          <p>{t('Ownership.InviteBuyer.SentBody', { firm })}</p>
          <button type="button" className="cc-own-primary" onClick={onClose}>
            {t('Ownership.InviteBuyer.BackToProject')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cc-own-modal-scrim" role="dialog" aria-modal="true">
      <div className="cc-own-modal">
        <button type="button" className="cc-own-x" onClick={onClose} aria-label={t('General.Close')}>
          <FontAwesomeIcon icon={faXmark} />
        </button>

        <h2>{t('Ownership.InviteBuyer.Title')}</h2>
        <p>{t('Ownership.InviteBuyer.Lead', { firm })}</p>

        <div className="cc-own-why">
          <b>{t('Ownership.InviteBuyer.WhyLabel')}</b> {t('Ownership.InviteBuyer.Why')}
        </div>

        <label className="cc-own-field">
          <span>{t('Ownership.InviteBuyer.Name')}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
        </label>

        <label className="cc-own-field">
          <span>{t('Ownership.InviteBuyer.Role', { firm })}</span>
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`Ownership.InviteBuyer.Roles.${r}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="cc-own-field">
          <span>{t('Ownership.InviteBuyer.Email')}</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="off" />
        </label>

        <label className="cc-own-field">
          <span>{t('Ownership.InviteBuyer.Note')}</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
        </label>

        {error ? <div className="cc-own-error">{error}</div> : null}

        <div className="cc-own-modal-actions">
          <button type="button" className="cc-own-secondary" onClick={onClose}>
            {t('General.Cancel')}
          </button>
          <button type="button" className="cc-own-primary" onClick={send} disabled={!valid || busy}>
            {busy ? t('Ownership.InviteBuyer.Sending') : t('Ownership.InviteBuyer.Send')}
          </button>
        </div>
      </div>
    </div>
  );
};
